import React, { forwardRef } from 'react';
import { useButton, useHover } from 'react-aria';
import { castRef } from '@direct-frontend/stdlib';

import { useObjectRef } from '../../../hooks/useObjectRef';
import { cn } from '../../../lib/css/className/className';
import { HStack } from '../../Stack';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import './CommonTrigger.css';
import { SkinColor, SkinRole } from '../../../lib/skin/getColorSkin/generated/types';
import { SelectColor } from '../commonTypes';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { useFocusOptions } from '../../../hooks/useFocusOptions';

import { CommonTriggerProps } from './types';

export const cnCommonTrigger = cn('SelectCommonTrigger');

const skinMap: Record<SelectColor, SkinColor | SkinRole> = {
    contour: 'gray-transparent-contour',
    contrast: 'gray-neutral',
    normal: 'gray-low-contrast',
};

export const CommonTrigger = forwardRef(function CommonTrigger<ValueType extends string>(
    props: CommonTriggerProps<ValueType>,
    forwardedRef: React.ForwardedRef<HTMLElement>,
) {
    const { size, color, isDisabled, isTruncated, validationState, width, children, className, id } = props;

    const sizeSkinClass = getSizeSkin(size);

    const ref = useObjectRef(forwardedRef);
    const { buttonProps, isPressed } = useButton(props, ref);
    const { hoverProps, isHovered } = useHover({ isDisabled });
    // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
    const { focusProps, isFocusVisible } = useFocusOptions();

    const colorSkinClass = getColorSkin(skinMap[color], { isHovered: isHovered || isPressed, isDisabled });

    return (
        <button
            data-testid={props['data-testid']}
            {...buttonProps}
            {...hoverProps}
            {...focusProps}
            id={id}
            className={cnCommonTrigger(
                {
                    size,
                    width,
                    state: validationState === 'invalid' && 'error',
                    hovered: isHovered,
                    focused: isFocusVisible,
                    disabled: isDisabled,
                    truncated: isTruncated,
                },
                [className, sizeSkinClass, colorSkinClass],
            )}
            ref={castRef(ref, 'button')}
        >
            <HStack justifyContent={'flex-start'} alignItems={'flex-start'} gap={'current-geometry'}>
                {children}
            </HStack>
        </button>
    );
});
